/*
 * GATE GLITCH · magia de la pantalla "Entrar" — yanipaper
 * =======================================================
 * Dibuja la imagen del gate (docs/entrar.webp, ya con bordes fundidos a negro) en un canvas CONTAIN
 * (cabe entera, centrada) y le mete un GLITCH confinado a DOS zonas: la SILUETA de la persona (tercio
 * izquierdo) y la RASPBERRY voladora que lanza (cúmulo brillante a la derecha). Cortes de franjas +
 * aberración cromática magenta/cian, en ráfagas cortas e intermitentes. El resto del cuadro queda quieto.
 *
 * Decisiones:
 *   - La imagen va CONTAIN (cabe entera, centrada); como sus bordes ya están fundidos a negro y el
 *     canvas es transparente sobre el #0a0a0a del gate, las barras laterales son INVISIBLES (sin costura).
 *   - El glitch se dibuja SOLO dentro del rectángulo de la figura (clip), así "tiembla" la persona, no
 *     todo el cuadro. Sobre negro puro el glitch no prendería igual: por eso se confina por geometría.
 *   - Ráfagas cortas (~180–260 ms) cada 1.6–3.6 s: entre ráfagas el canvas muestra la imagen limpia y
 *     se DETIENE el rAF (no gasta batería en idle). prefers-reduced-motion → imagen fija, sin glitch.
 *   - Se autodestruye cuando el gate se va (clase is-out o sale del DOM): no quedan timers vivos.
 *
 * No decide el ciclo de vida del gate (eso es intro.js): solo se engancha al canvas y se apaga solo.
 */

import entrarUrl from '../docs/entrar.webp';

// Zonas glitcheables en coordenadas normalizadas de la imagen (0..1). Solo estas "tiemblan":
//   - figura: la persona, tercio izquierdo (puño ~0.07, cabeza ~0.12–0.22, pierna ~0.30).
//   - raspberry: el cúmulo brillante que lanza, arriba-derecha (spray + objeto geométrico).
// Cada zona glitchea de forma independiente. El resto del cuadro queda quieto.
const REGIONS = [
  { key: 'figura',    x0: 0.02, x1: 0.34, y0: 0.03, y1: 0.99 },
  { key: 'raspberry', x0: 0.47, x1: 0.86, y0: 0.00, y1: 0.46 },
];

const rand = (a, b) => a + Math.random() * (b - a);
const randInt = (a, b) => Math.floor(rand(a, b + 1));

export function createGateGlitch({ canvas, reducedMotion = false }) {
  const ctx = canvas.getContext('2d', { alpha: true });
  let img = null;
  let dpr = 1;
  let destroyed = false;
  let raf = 0;
  let burstTimer = 0;
  // Rect de dibujo de la imagen (contain) y de cada zona glitcheable, en px de canvas. Recalc en fit().
  let draw = { x: 0, y: 0, w: 0, h: 0 };
  let rects = [];   // [{key,x,y,w,h}] paralelo a REGIONS

  function fit() {
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    if (!img) return;
    const cw = canvas.width, ch = canvas.height;
    const iw = img.naturalWidth, ih = img.naturalHeight;
    // CONTAIN: la imagen cabe ENTERA (escala por el lado más ajustado), centrada.
    const s = Math.min(cw / iw, ch / ih);
    draw.w = iw * s; draw.h = ih * s;
    draw.x = (cw - draw.w) / 2; draw.y = (ch - draw.h) / 2;
    // Rectángulo de cada zona en px de canvas.
    rects = REGIONS.map((r) => ({
      key: r.key,
      x: draw.x + r.x0 * draw.w,
      y: draw.y + r.y0 * draw.h,
      w: (r.x1 - r.x0) * draw.w,
      h: (r.y1 - r.y0) * draw.h,
    }));
    drawClean();
  }

  function drawClean() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (img) ctx.drawImage(img, draw.x, draw.y, draw.w, draw.h);
  }

  // Glitchea UNA zona (rect en px de canvas) con la intensidad dada. Todo confinado por clip.
  function glitchRegion(r, intensity) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(r.x, r.y, r.w, r.h);
    ctx.clip();

    // 1) Aberración cromática: dos copias desfasadas y teñidas hacia magenta y cian, compuestas con
    //    'lighten' (max por canal → NO blanquea el neón como 'screen'; solo agrega un fringe de color
    //    en los bordes). Sobre negro no cambia nada. El clip la recorta a esta zona.
    const a = 3 * dpr + intensity * 7 * dpr;      // desfase horizontal en px
    const supportsFilter = 'filter' in ctx;
    ctx.globalCompositeOperation = 'lighten';
    ctx.globalAlpha = 0.55;
    if (supportsFilter) ctx.filter = 'hue-rotate(300deg) saturate(1.8)'; // → magenta
    ctx.drawImage(img, draw.x - a, draw.y, draw.w, draw.h);
    if (supportsFilter) ctx.filter = 'hue-rotate(150deg) saturate(1.8)'; // → cian
    ctx.drawImage(img, draw.x + a, draw.y, draw.w, draw.h);
    if (supportsFilter) ctx.filter = 'none';
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // 2) Cortes de franjas: se copian bandas horizontales de la zona (del canvas ya pintado) y se
    //    redibujan corridas en x → efecto "desgarro".
    const slices = randInt(2, 4 + Math.round(intensity * 3));
    for (let i = 0; i < slices; i++) {
      const sy = rand(r.y, r.y + r.h);
      const sh = rand(4 * dpr, (10 + intensity * 26) * dpr);
      const off = rand(-1, 1) * (6 + intensity * 22) * dpr;
      ctx.drawImage(canvas, r.x, sy, r.w, sh, r.x + off, sy, r.w, sh);
    }
    ctx.restore();
  }

  // Un fotograma de glitch: parte de la imagen limpia y glitchea cada zona por separado. Cada zona
  // recibe su propia intensidad (desfase aleatorio) para que no "latan" sincronizadas.
  function drawGlitchFrame(intensity) {
    drawClean();
    if (!img) return;
    for (const r of rects) {
      // pequeña variación por zona: la raspberry glitchea un poco más nervioso que la figura
      const jitter = r.key === 'raspberry' ? rand(0.85, 1.25) : rand(0.7, 1.05);
      glitchRegion(r, Math.min(1, intensity * jitter));
    }
  }

  // Programa la siguiente ráfaga. Entre ráfagas NO hay rAF (idle barato).
  function scheduleBurst() {
    if (destroyed || reducedMotion) return;
    const gap = rand(1600, 3600);
    burstTimer = window.setTimeout(runBurst, gap);
  }

  function runBurst() {
    if (destroyed) return;
    if (gateGone()) { destroy(); return; }
    const dur = rand(160, 260);
    const t0 = performance.now();
    const step = () => {
      if (destroyed) return;
      const t = performance.now() - t0;
      if (t >= dur) { drawClean(); scheduleBurst(); return; }
      // intensidad con una envolvente que sube y baja dentro de la ráfaga
      const k = t / dur;
      const env = Math.sin(k * Math.PI);
      // ~18 fps de glitch basta y se ve más "digital" que 60fps; redibuja en cada rAF igualmente
      drawGlitchFrame(0.35 + env * 0.65);
      raf = requestAnimationFrame(step);
    };
    step();
  }

  function gateGone() {
    const gate = document.getElementById('gate');
    return !gate || gate.classList.contains('is-out');
  }

  const onResize = () => fit();

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (raf) cancelAnimationFrame(raf);
    if (burstTimer) clearTimeout(burstTimer);
    window.removeEventListener('resize', onResize);
  }

  // Arranque: carga la imagen, pinta limpio y programa la primera ráfaga.
  const el = new Image();
  el.decoding = 'async';
  el.onload = () => {
    if (destroyed) return;
    img = el;
    fit();
    if (!reducedMotion) scheduleBurst();
  };
  el.src = entrarUrl;

  window.addEventListener('resize', onResize);
  fit();

  return { destroy };
}
